import { useEffect } from "react";
import { Nav } from "@/components/site/Nav";
import { Hero } from "@/components/site/Hero";
import { Stats } from "@/components/site/Stats";
import { Pricing } from "@/components/site/Pricing";
import { ReferralPromo } from "@/components/site/ReferralPromo";
import { Download } from "@/components/site/Download";
import { Reviews } from "@/components/site/Reviews";
import { FreeScanPromo } from "@/components/site/FreeScanPromo";
import { HowItWorks } from "@/components/site/HowItWorks";
import { FAQ } from "@/components/site/FAQ";
import { Footer } from "@/components/site/Footer";
import { StickyMobileCTA } from "@/components/site/StickyMobileCTA";
import { storeReferralCode } from "@/lib/referral";
import { useSeo } from "@/lib/seo";

export default function Landing() {
  useSeo({
    title: "Flow Tweaks — PC & Console Optimization for Competitive Players",
    description:
      "Real FPS gains for competitive Fortnite and FPS players. Download the app free, unlock a plan from $5 — one-time, no subscriptions, 27+ five-star reviews.",
    path: "/",
  });

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) storeReferralCode(ref);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Nav />
      <main>
        <Hero />

        {/* Savings Section */}
        <section className="relative py-24 overflow-hidden">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at top, rgba(255,94,0,0.06), transparent 65%)" }}
          />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
            <p className="text-orange-500 font-bold tracking-[0.35em] text-xs uppercase mb-3">
              Why Flow Tweaks
            </p>
            <h2
              className="text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] mb-12"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              Skip the <span className="text-orange-500">$300</span> upgrade.
              <br />
              <span className="text-zinc-500">Keep</span> the gains.
            </h2>

            <div className="grid grid-cols-3 items-center gap-4 w-full max-w-lg mb-8">
              <div className="glass p-5 text-center !rounded-xl">
                <p className="text-[8px] tracking-[0.25em] text-zinc-500 uppercase font-bold mb-2">New GPU</p>
                <p className="text-xs text-zinc-500 mb-2">RTX 4060</p>
                <p
                  className="text-2xl font-black line-through text-zinc-500"
                  style={{ fontFamily: "'Orbitron', sans-serif" }}
                >
                  $299
                </p>
              </div>

              <p className="text-xs font-black text-zinc-500 text-center">VS</p>

              <div className="glass p-5 text-center !rounded-xl" style={{ borderColor: "rgba(255,94,0,0.3)" }}>
                <p className="text-[8px] tracking-[0.25em] text-orange-500 uppercase font-bold mb-2">Flow Tweaks</p>
                <p className="text-xs text-zinc-400 mb-2">Full PC Optimization</p>
                <p
                  className="text-2xl font-black text-orange-500"
                  style={{ fontFamily: "'Orbitron', sans-serif" }}
                >
                  $15
                </p>
              </div>
            </div>

            <div className="glass grid grid-cols-3 w-full max-w-lg !rounded-xl overflow-hidden mb-6">
              {[
                // Derived from the comparison directly above: $15 against a $299 RTX 4060.
                // If either figure changes, this one has to change with it.
                { val: "95%", label: "Avg cost saved", accent: true },
                { val: "+35%", label: "Avg FPS gained", accent: false },
                { val: "150+", label: "Players optimized", accent: true },
              ].map((s) => (
                <div key={s.label} className="py-5 px-3 text-center border-r border-white/5 last:border-r-0">
                  <div
                    className={`text-xl font-black ${s.accent ? "text-orange-500" : "text-white"}`}
                    style={{ fontFamily: "'Orbitron', sans-serif" }}
                  >
                    {s.val}
                  </div>
                  <div className="text-[8px] uppercase tracking-[0.2em] text-zinc-400 mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-500">
              Results may vary · Not a hardware replacement
            </p>
          </div>
        </section>

        <Stats />
        <FreeScanPromo />
        <HowItWorks />
        <Pricing />
        <ReferralPromo />
        <Download />
        <Reviews />
        <FAQ />
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
